import {Component, OnDestroy, ViewChild} from '@angular/core';
import {Subject, takeUntil} from "rxjs";
import {Store} from "@ngrx/store";
import * as fromRoot from "../../../../../../state/app.state";
import {
  FetchParticipantsWeightListRequest
} from '../../../core/clients-list.actions';
import {participantsWeightList} from '../../../core/clients-list.selectors';
import {
  indicatorColors,
  roles,
  sortOrderMap,
  statusLabelMap,
  userStatusMap
} from '../../../../../shared/constants/common.constants';
import {CommonService} from '../../../../../shared/services/common.service';
import {ClientListService} from '../../../services/client-list.service';
import {Router} from '@angular/router';
import {
  ParticipantAndCaregiver
} from '../../../../../../models/participantAndCaregiver';
import {
  ParticipantsWeightList
} from '../../../../../../models/participants-weight-list';
import {MatSort, Sort} from "@angular/material/sort";
import {drillDownUrls} from "../../../../../shared/core/shared.selectors";
import {SetDrillDownUrls} from "../../../../../shared/core/shared.actions";
import {MatTable, MatTableDataSource} from "@angular/material/table";

@Component({
  selector: 'app-participant-weight-information-table',
  templateUrl: './participant-weight-information-table.component.html',
  styleUrls: ['./participant-weight-information-table.component.css']
})
export class ParticipantWeightInformationTableComponent implements OnDestroy {
  @ViewChild(MatTable) table: MatTable<any>;
  @ViewChild(MatSort) sort: MatSort;

  loading: boolean = false;
  participantCaregiverList: ParticipantsWeightList;
  typeMap = {
    4: 'Adult',
    5: 'Participant',
  };
  tableData: any[];
  dataSource: any = new MatTableDataSource();
  displayedColumns = [
    'name',
    'group',
    'currentWeight',
    'weightChange',
    'weightLossAlert',
    'currentBMI',
    'changeBMI'
  ];

  participantOnlyColumns = [
    'currentMedianBMI',
    'changeMedianBMI',
    'currentOverPercentile',
    'changeOverPercentile'
  ]

  isFirstPage = true;

  selectedUserId: number | null;
  selectedUserRole: number | null;
  role: number;
  roles = roles;
  userRole: number;
  sortOrder: any = 1;
  sortColumn: any = 'name'
  protected readonly clinicStatusLabelMap = statusLabelMap;
  protected readonly userStatusMap = userStatusMap;
  private readonly OnDestroy: Subject<any> = new Subject<any>();
  private participantWeightFilters: {
    clientId?: number[],
    groupId?: number[],
    session?: number,
    status?: number[],
    searchText?: string,
  } = null;

  constructor(
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private clientListService: ClientListService,
    private router: Router,
  ) {
    this.store.select(participantsWeightList)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(data => {
        this.loading = data.loading;
        this.participantCaregiverList = data;


        if (!data.loading) {
          this.dataSource.data = [...data.list];

          if (data.hasNext) {
            setTimeout(() => {
              if (this.sortColumn && data.pageNumber === 1) {
                this.commonService.scrollColumnInView(this.sortColumn);
              }
            }, 0)

            setTimeout(() => {
              // check window height for infinite scroll
              this.commonService.checkInfiniteScrollHeightAndCallNext('table-wrapper', () => {
                this.onScroll();
              });
            }, 0)
          }
        }
      });

    // listen for filter changes from the filter component
    this.clientListService.onParticipantWeightInformationFilterChange.pipe(takeUntil(this.OnDestroy))
      .subscribe((filters) => {
        this.participantWeightFilters = filters;

        this.isFirstPage = true;

        this.displayedColumns = this.displayedColumns.filter(column => !this.participantOnlyColumns.includes(column));

        if (filters['role']?.length && filters['role'].includes(roles.participant)) {
          this.displayedColumns = [
            ...this.displayedColumns,
            ...this.participantOnlyColumns
          ]
        }
        this.participantWeightFilters = filters;
        this.fetchParticipantWeightList();
      })

  }

  fetchParticipantWeightList(pageNumber = 1, limit = 10) {
    this.store.dispatch(FetchParticipantsWeightListRequest({
      payload: {
        ...this.participantWeightFilters,
        pageNumber,
        limit,
        ...(this.sortOrder ? {sortOrder: this.sortOrder} : {}),
        ...(this.sortOrder ? {sortColumn: this.sortColumn} : {}),
      }
    }));
  }

  onScroll() {
    if (this.participantCaregiverList?.hasNext && !this.participantCaregiverList?.loading) {
      this.fetchParticipantWeightList(this.participantCaregiverList.pageNumber + 1, this.participantCaregiverList.limit);
      this.isFirstPage = false;
    }
  }

  openUserDetails(user: ParticipantAndCaregiver) {
    if (user?.id) {
      this.store.select(drillDownUrls).subscribe(data => {
        const updatedDrillDownUrls = [...data, this.router.url];
        if ([...data].pop() !== this.router.url) {
          this.store.dispatch(SetDrillDownUrls({payload: updatedDrillDownUrls}));
        }
      }).unsubscribe();

      const urlPrefix = this.router.url.split('/')[1];
      this.router.navigate([`/${urlPrefix}/member/${user.id}/behavior-tracker`]);
    }
  }

  groupNameClick(data) {
    if (data?.group?.id) {
      this.store.select(drillDownUrls).subscribe(data => {
        const updatedDrillDownUrls = [...data, this.router.url];
        if ([...data].pop() !== this.router.url) {
          this.store.dispatch(SetDrillDownUrls({payload: updatedDrillDownUrls}));
        }
      }).unsubscribe();

      const urlPrefix = this.router.url.split('/')[1];
      this.router.navigate([`${urlPrefix}/group/${data.group.id}/dashboard`])
    }
  }

  sortData(sort: Sort) {
    this.isFirstPage = true;
    this.sortColumn = `${sort.active}`;
    this.sortOrder = sortOrderMap[sort.direction];

    this.fetchParticipantWeightList();
  }

  ngOnDestroy() {
    this.OnDestroy.next(null);
    this.OnDestroy.complete();
  }
}
